<script setup lang="ts">
import ErrorComponent from '~/components/feedbacks/ErrorComponent.vue'
import type { NuxtError } from '#app'
import { IconNames } from '~/enums/icons/whitelisted'
import { useSessionStore } from '~/stores/session'

/**
 * Page to handle FATAL errors
 * Therefore not caught by the ErrorBoundary
 * Examples of usages:
 * If the {fatal:true} is added on the createError
 * If the error is wrapped in the showError() util
 * If there is a 500 not caught on the ErrorBoundary(initiated from a nuxt page)
 */
const error = useError()

const { t } = useI18n()
const { contacts } = useBusinessRules()
const { logout } = useSessionStore()

const errorComponent = computed(() => {
  if (!error.value)
    return null
  return ErrorComponent
})

const defaultLinks = computed(() => {
  if (!error.value)
    return []

  const _actions = [
    {
      label: t('error-component.secondary-cta'),
      color: 'neutral',
      variant: 'link',
      to: `mailto:${contacts.value.support}`,
    },
  ]

  if (error.value.status === 404) {
    _actions.unshift({
      label: t('error-component.tertiary-cta'),
      color: 'neutral',
      variant: 'solid',
      onClick: async () => {
        window.location.href = window.location.origin
        await clearError()
      },
    })
  }
  else {
    _actions.unshift({
      label: t('error-component.main-cta'),
      color: 'neutral',
      variant: 'solid',
      onClick: async () => {
        await logout(true)
        await clearError()
      },
    })
  }
  return _actions
})

function shapeError(err: Error | NuxtError) {
  // set the error if it was already shaped/handled
  if (err?.statusText) {
    const _err = err as NuxtError
    return {
      title: _err.statusText!,
      description: _err.message,
      headline: _err.data?.headline,
      icon: _err.data?.icon,
      links: _err.data?.links || defaultLinks.value,
    }
  }

  // Generic
  return {
    headline: t('error-component.headline'),
    title: t('error-component.title'),
    description: t('error-component.body'),
    icon: IconNames.refresh_ccw_01,
    links: defaultLinks.value,
  }
}
</script>

<template>
  <div class="h-screen overflow-y-auto">
    <component :is="errorComponent" v-bind="shapeError(error)" v-if="error" class="h-full" />
  </div>
</template>
